import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import './index.css'
import { Buffer } from 'buffer'
import App from './App.jsx'

// Polyfill Buffer for Deepgram SDK
window.Buffer = Buffer

// Preload critical resources
const preloadCriticalResources = () => {
  // Preload fonts
  const fontLink = document.createElement('link');
  fontLink.rel = 'preload';
  fontLink.as = 'font';
  fontLink.type = 'font/woff2';
  fontLink.crossOrigin = 'anonymous';
  document.head.appendChild(fontLink);

  // Preconnect to external domains
  const preconnectDomains = ['https://fonts.googleapis.com', 'https://fonts.gstatic.com'];
  preconnectDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = domain;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Initialize performance optimizations
preloadCriticalResources();

// Register service worker for PWA
if ('serviceWorker' in navigator) {
  const updateSW = registerSW({
    onNeedRefresh() {
      // Show update notification to user using modal (don't auto-update)
      const showUpdateModal = () => {
        if (window.showGlobalModal) {
          window.showGlobalModal({
            title: 'Update Available',
            message: 'A new version of the app is available. Would you like to update now?',
            type: 'info',
            showCancel: true,
            onConfirm: (confirmed) => {
              if (confirmed) {
                updateSW(true);
              }
            },
          });
        } else {
          // Fallback: retry after a short delay if modal system isn't ready yet
          setTimeout(showUpdateModal, 1000);
        }
      };
      // Don't show update modal immediately, wait for user interaction
      setTimeout(showUpdateModal, 5000);
    },
    onOfflineReady() {
      // console.log('App ready to work offline');
    },
  });
}

// Register Periodic Background Sync for notifications even when page is closed
async function setupBackgroundNotifications() {
  try {
    if (!('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.ready;

    // Request notification permission if not yet decided
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }

    // Seed IndexedDB with first-visit marker + notification messages
    // so the SW can show notifications even if the page is never reopened
    const db = await new Promise((resolve, reject) => {
      const req = indexedDB.open('TinatNotifications', 1);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = (e) => {
        const d = e.target.result;
        if (!d.objectStoreNames.contains('notifications')) {
          d.createObjectStore('notifications', { keyPath: 'id' });
        }
      };
    });

    // Mark first visit if not already marked
    const tx1 = db.transaction(['notifications'], 'readwrite');
    const store1 = tx1.objectStore('notifications');
    const existing = await new Promise((r) => {
      const g = store1.get('firstVisitTime');
      g.onsuccess = () => r(g.result);
      g.onerror = () => r(null);
    });
    if (!existing) {
      store1.put({ id: 'firstVisitTime', timestamp: Date.now() });
    }

    // Store notification messages in IndexedDB for the SW to read
    const { default: notificationMessages } = await import('./data/notificationMessages.json');
    // Also import the dailyNotificationService messages as fallback
    const NOTIFICATION_MESSAGES = {
      guest: (notificationMessages.guest || []).map(m => ({
        title: m.title,
        body: m.message || m.body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: 'daily-guest',
        url: m.link || m.url || '/register'
      })),
      loggedIn: Object.entries(notificationMessages.loggedIn || {}).reduce((acc, [key, msgs]) => {
        acc[key] = msgs.map(m => ({
          title: m.title,
          body: m.message || m.body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'daily-logged',
          url: m.link || m.url || '/courses'
        }));
        return acc;
      }, {}),
    };

    const tx2 = db.transaction(['notifications'], 'readwrite');
    tx2.objectStore('notifications').put({
      id: 'notificationConfig',
      messages: NOTIFICATION_MESSAGES,
      interval: 24 * 60 * 60 * 1000,
      updatedAt: Date.now()
    });

    // Register Periodic Background Sync (Chrome/Edge 80+)
    if ('periodicSync' in registration) {
      try {
        const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
        if (status.state === 'granted') {
          await registration.periodicSync.register('daily-notification-sync', {
            minInterval: 24 * 60 * 60 * 1000, // 24 hours
          });
          console.log('[Main] Periodic background sync registered');
        }
      } catch (e) {
        console.warn('[Main] Periodic sync registration failed:', e);
      }
    }

    // Also tell the SW to do the scheduling (fallback for browsers without periodicSync)
    if (registration.active) {
      registration.active.postMessage({
        type: 'SCHEDULE_DAILY_NOTIFICATIONS',
        data: {
          messages: NOTIFICATION_MESSAGES,
          interval: 24 * 60 * 60 * 1000
        }
      });
    }
  } catch (error) {
    console.warn('[Main] Background notification setup failed:', error);
  }
}

// Kick off background notification setup after a short delay to not block initial render
setTimeout(setupBackgroundNotifications, 3000);

// Use concurrent features for better performance
const root = createRoot(document.getElementById('root'));
root.render(
  <StrictMode>
    <App />
  </StrictMode>
);
